import React, { useState, useEffect, useRef, useContext } from "react";
import { AppContext } from "../../context/AppContext";
import MetricsCard from "../MetricsCard/MetricsCard";
import "./PublicationDetails.scss";
import { KEY_NAME } from "../../utils/KeyName";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import { useCustomSearchParams } from "../hooks/useCustomSearchParams";
import Loader from "../Loader/Loader";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function activateMetrics(key) {
  switch (key) {
    case KEY_NAME.publiclyAccessibleYes:
    case KEY_NAME.publiclyAccessibleUnknown:
      return "verified";

    case KEY_NAME.licenseYes:
    case KEY_NAME.licenseUnknown:
      return "reuse";

    case KEY_NAME.archiveYes:
    case KEY_NAME.archiveUnknown:
      return "archived";

    case KEY_NAME.workCount:
      return "total";

    case KEY_NAME.onFunderPortal:
      return "funder_portal";

    case KEY_NAME.okOrcid:
      return "ok_orcid";

    case KEY_NAME.okDatasetCount:
      return "ok_dataset";

    default:
      return "total";
  }
}

const PublicationMetrics = ({
  publisherCount,
  matricsKey,
  setSelectedPublisher,
  metricsLoading,
}) => {
  const { entityName } = useContext(AppContext);
  const { get } = useCustomSearchParams();

  const scrollRef = useRef(null);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const [selected, setSelected] = useState({
    [activateMetrics(get(SEARCH_PARAMS.ACTIVE_METRICS_CARD) || matricsKey)]:
      get(SEARCH_PARAMS.ACTIVE_METRICS_CARD) || matricsKey,
  });

  useEffect(() => {
    if (matricsKey) {
      setSelected({ [activateMetrics(matricsKey)]: matricsKey });
    }
  }, [matricsKey]);

  /* ---------- Scroll Logic ---------- */

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    const hasOverflow = scrollWidth > clientWidth + 5;

    if (!hasOverflow) {
      setShowLeft(false);
      setShowRight(false);
      return;
    }

    setShowLeft(scrollLeft > 5);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 5);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  /* Important: recheck after data renders */
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScroll();
    }, 150);

    return () => clearTimeout(timer);
  }, [publisherCount, metricsLoading]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollBy({
      left: direction === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  /* ---------- Selection ---------- */

  const handleSelect = (metric, value, count) => {
    setSelected({ [metric]: value });
    setSelectedPublisher(value);
  };

  const renderOption = (metric, value, label, count) => {
    const isChecked = selected[metric] === value;

    return (
      <label
        className={`metric-option ${isChecked ? "active" : ""}`}
        onClick={() => handleSelect(metric, value, count)}
      >
        <input type="radio" name={metric} checked={isChecked} readOnly />
        <div className="option-content">
          <span className="option-label">{label}</span>
          <span className="option-count">{count}</span>
        </div>
      </label>
    );
  };

  return (
    <div className="metrics-scroll-wrapper">
      {/* {showLeft && (
        <button
          className="scroll-btn left"
          onClick={() => scroll("left")}
          aria-label="Scroll left"
        >
          <ChevronLeft size={20} />
        </button>
      )} */}

      {(showLeft || showRight) && (
        <span
          className={`slide-btn left ${!showLeft && "disabled"}`}
          onClick={() => scroll("left")}
          aria-label="Scroll left"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </span>
      )}

      <div className="publication-metrics scrollable" ref={scrollRef}>
        {/* Total */}
        <MetricsCard
          title="Total Publications"
          value={Number(publisherCount?.workCount || 0).toLocaleString("en-US")}
          activeMetricsCard={selected.total}
          setActiveMetricsCard={() => handleSelect("total", "workCount")}
          metricsLoading={metricsLoading}
          align="center"
        />

        {/* Verified OA */}
        <div
          className={`metric-card ${
            selected.verified ? "active-metrics-card" : ""
          }`}
        >
          <div className="metric-title text-center">
            Verified Open Access on Publisher’s Site
          </div>

          {metricsLoading ? (
            <Loader />
          ) : (
            <div className="metric-split">
              {renderOption(
                "verified",
                KEY_NAME.publiclyAccessibleYes,
                "Yes",
                Number(
                  publisherCount?.[KEY_NAME.publiclyAccessibleYes] || 0,
                ).toLocaleString("en-US"),
              )}

              <span className="divider" />

              {renderOption(
                "verified",
                KEY_NAME.publiclyAccessibleUnknown,
                "Unknown/No",
                Number(
                  publisherCount?.[KEY_NAME.publiclyAccessibleUnknown] || 0,
                ).toLocaleString("en-US"),
              )}
            </div>
          )}
        </div>

        {/* Reuse */}
        <div
          className={`metric-card ${
            selected.reuse ? "active-metrics-card" : ""
          }`}
        >
          <div className="metric-title text-center">Reuse Terms Available</div>

          {metricsLoading ? (
            <Loader />
          ) : (
            <div className="metric-split">
              {renderOption(
                "reuse",
                KEY_NAME.licenseYes,
                "Yes",
                Number(
                  publisherCount?.[KEY_NAME.licenseYes] || 0,
                ).toLocaleString("en-US"),
              )}

              <span className="divider" />

              {renderOption(
                "reuse",
                KEY_NAME.licenseUnknown,
                "Unknown/No",
                Number(
                  publisherCount?.[KEY_NAME.licenseUnknown] || 0,
                ).toLocaleString("en-US"),
              )}
            </div>
          )}
        </div>

        {/* Archived */}
        <div
          className={`metric-card ${
            selected.archived ? "active-metrics-card" : ""
          }`}
        >
          <div className="metric-title text-center">Archived</div>

          {metricsLoading ? (
            <Loader />
          ) : (
            <div className="metric-split">
              {renderOption(
                "archived",
                KEY_NAME.archiveYes,
                "Yes",
                Number(
                  publisherCount?.[KEY_NAME.archiveYes] || 0,
                ).toLocaleString("en-US"),
              )}

              <span className="divider" />

              {renderOption(
                "archived",
                KEY_NAME.archiveUnknown,
                "Unknown/No",
                Number(
                  publisherCount?.[KEY_NAME.archiveUnknown] || 0,
                ).toLocaleString("en-US"),
              )}
            </div>
          )}
        </div>

        {/* OrcID */}
        {/* {publisherCount?.okOrcid >= 0 && ( <MetricsCard
          title="Orcid"
          value={Number(publisherCount?.okOrcid || 0).toLocaleString("en-US")}
          activeMetricsCard={selected.ok_orcid}
          setActiveMetricsCard={() => handleSelect("ok_orcid", "okOrcid")}
          metricsLoading={metricsLoading}
          align="center"
        />
        )} */}
        <div
          className={`metric-card ${
            selected.ok_orcid ? "active-metrics-card" : ""
          }`}
        >
          <div className="metric-title text-center">ORCID</div>

          {metricsLoading ? (
            <Loader />
          ) : (
            <div className="metric-split one-option">
              {renderOption(
                "ok_orcid",
                KEY_NAME.okOrcid,
                "Total",
                Number(publisherCount?.[KEY_NAME.okOrcid] || 0).toLocaleString(
                  "en-US",
                ),
              )}
            </div>
          )}
        </div>

        <div
          className={`metric-card ${
            selected.ok_dataset ? "active-metrics-card" : ""
          }`}
        >
          <div className="metric-title text-center">Dataset</div>

          {metricsLoading ? (
            <Loader />
          ) : (
            <div className="metric-split one-option">
              {renderOption(
                "ok_dataset",
                KEY_NAME.okDatasetCount,
                "Total",
                Number(
                  publisherCount?.[KEY_NAME.okDatasetCount] || 0).toLocaleString("en-US"),
              )}
            </div>
          )}
        </div>

        {/* Funder Portal */}
        {/* {publisherCount?.onFunderPortal >= 0 && (
          <MetricsCard
            title="On Funder Portal"
            value={Number(publisherCount?.onFunderPortal || 0).toLocaleString(
              "en-IN",
            )}
            activeMetricsCard={selected.funder_portal}
            setActiveMetricsCard={() =>
              handleSelect("funder_portal", "onFunderPortal")
            }
            metricsLoading={metricsLoading}
            align="center"
          />
        )} */}
        {publisherCount?.onFunderPortal >= 0 && (
          <div
            className={`metric-card ${
              selected.funder_portal ? "active-metrics-card" : ""
            }`}
          >
            <div className="metric-title text-center">On Funder Portal</div>

            {metricsLoading ? (
              <Loader />
            ) : (
              <div className="metric-split one-option">
                {renderOption(
                  "funder_portal",
                  KEY_NAME.onFunderPortal,
                  "Total",
                  Number(
                    publisherCount?.[KEY_NAME.onFunderPortal] || 0,
                  ).toLocaleString("en-US"),
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {(showRight || showLeft) && (
        <span
          className={`slide-btn right ${!showRight && "disabled"}`}
          onClick={() => scroll("right")}
          aria-label="Scroll right"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </span>
      )}

      {/* {showRight && (
        <button
          className="scroll-btn right"
          onClick={() => scroll("right")}
          aria-label="Scroll right"
        >
          <ChevronRight size={20} />
        </button>
      )} */}
    </div>
  );
};

export default PublicationMetrics;
